import React from 'react';
import type { LocationForecastBlock } from './weatherForecastUtils';
import { fetchLocationForecastBlocks } from './fetchLocationForecastBlocks';

function locationsKey(locations: string[]): string {
  return JSON.stringify(locations.map((s) => String(s || '').trim()).filter(Boolean));
}

/**
 * Loads multi-location 5-day forecasts when `enabled` is true; refreshes on interval.
 */
export function useWeatherForecastBlocks(
  locations: string[],
  options: { enabled: boolean; refreshMs?: number }
): { blocks: LocationForecastBlock[] | 'loading' | null; reload: () => void } {
  const { enabled, refreshMs = 10 * 60 * 1000 } = options;
  const [blocks, setBlocks] = React.useState<LocationForecastBlock[] | 'loading' | null>(null);
  const key = locationsKey(locations);

  const reload = React.useCallback(async () => {
    setBlocks('loading');
    try {
      const list = JSON.parse(key) as string[];
      const out = await fetchLocationForecastBlocks(Array.isArray(list) ? list : []);
      setBlocks(out);
    } catch {
      setBlocks([]);
    }
  }, [key]);

  React.useEffect(() => {
    if (!enabled) {
      setBlocks(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setBlocks('loading');
      try {
        const out = await fetchLocationForecastBlocks(JSON.parse(key) as string[]);
        if (!cancelled) setBlocks(out);
      } catch {
        if (!cancelled) setBlocks([]);
      }
    };
    run();
    const timer = window.setInterval(run, refreshMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [enabled, key, refreshMs]);

  return { blocks, reload };
}
